import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { DateRange } from '~/components/TableFilters/types';
import { relationshipSummaryModel } from '~/models';
import { AmendmentsStatusFilterValue, AutomaticFilterValue, BillingTriggerFilterValue, CompletionFilterValue, FilterType, HasCreditFilterValue, LastBillingDateType, PaymentMethodFilterValue, PriceIncreaseFilterValue, RecurrenceFilterValue, toApprovalDisplayDate } from '~/pages/AgreementsDashboard/agreementsDashboardFilters';
import { serviceNamesToSet } from '../chips/agreementServiceName';
const STATUS_MAP = {
    active: relationshipSummaryModel.AgreementStatus.Active,
    amended: relationshipSummaryModel.AgreementStatus.Active,
    exhausted: relationshipSummaryModel.AgreementStatus.Exhausted,
    terminated: relationshipSummaryModel.AgreementStatus.Terminated
};
const PAYMENT_METHOD_MAP = {
    bank: PaymentMethodFilterValue.BankAccount,
    credit: PaymentMethodFilterValue.CreditCard,
    notSet: PaymentMethodFilterValue.NotSet,
    verificationInitiated: PaymentMethodFilterValue.VerificationInitiated,
    verificationFailed: PaymentMethodFilterValue.VerificationFailed
};
const HAS_CREDIT_MAP = {
    yes: HasCreditFilterValue.Yes,
    no: HasCreditFilterValue.No
};
const AMENDMENT_STATUS_MAP = {
    amendmentDraft: AmendmentsStatusFilterValue.AmendmentDraft,
    awaitingApproval: AmendmentsStatusFilterValue.AwaitingApproval,
    noChanges: AmendmentsStatusFilterValue.NoChanges
};
const BILLED_UPON_TRIGGER_MAP = {
    ongoing: BillingTriggerFilterValue.Automatic,
    approval: BillingTriggerFilterValue.Automatic,
    oneTimeInStages: BillingTriggerFilterValue.Automatic,
    oneTimeManual: BillingTriggerFilterValue.Manual,
    oneTimeDelivery: BillingTriggerFilterValue.Manual,
    repeatableManual: BillingTriggerFilterValue.Manual,
    onDemand: BillingTriggerFilterValue.Manual,
    inStagesManual: BillingTriggerFilterValue.Manual,
    inStagesPartlyAutomatic: BillingTriggerFilterValue.Manual,
    inStagesAutomatic: BillingTriggerFilterValue.Automatic
};
const BILLED_UPON_OCCURRENCE_MAP = {
    oneTimeManual: RecurrenceFilterValue.OneTime,
    oneTimeDelivery: RecurrenceFilterValue.OneTime,
    oneTimeInStages: RecurrenceFilterValue.OneTime,
    approval: RecurrenceFilterValue.OneTime,
    inStagesManual: RecurrenceFilterValue.OneTime,
    inStagesPartlyAutomatic: RecurrenceFilterValue.OneTime,
    inStagesAutomatic: RecurrenceFilterValue.OneTime,
    onDemand: RecurrenceFilterValue.OnDemand
};
const RECURRENCE_MAP = {
    weekly: RecurrenceFilterValue.Weekly,
    biweekly: RecurrenceFilterValue.Biweekly,
    monthly: RecurrenceFilterValue.Monthly,
    quarterly: RecurrenceFilterValue.Quarterly,
    yearly: RecurrenceFilterValue.Yearly,
    oneTime: RecurrenceFilterValue.OneTime,
    onDemand: RecurrenceFilterValue.OnDemand
};
const COMPLETION_MAP = {
    completed: CompletionFilterValue.Completed,
    notCompleted: CompletionFilterValue.NotCompleted
};
const AUTOMATIC_STATE_MAP = {
    active: AutomaticFilterValue.Active,
    paused: AutomaticFilterValue.Paused
};
const mapStatus = (status)=>{
    if ((status === null || status === void 0 ? void 0 : status.in) == null) return {};
    return {
        [FilterType.AgreementStatus]: [
            ...new Set(status.in.map((value)=>STATUS_MAP[value]))
        ]
    };
};
const mapEffectiveDate = (effectiveDate)=>{
    if ((effectiveDate === null || effectiveDate === void 0 ? void 0 : effectiveDate.gte) == null && (effectiveDate === null || effectiveDate === void 0 ? void 0 : effectiveDate.lte) == null) return {};
    const value = _object_spread({}, effectiveDate.gte != null && {
        fromDate: new Date(effectiveDate.gte)
    }, effectiveDate.lte != null && {
        toDate: new Date(effectiveDate.lte)
    });
    return {
        [FilterType.EffectiveDate]: value
    };
};
const mapApprovalDate = (approvalDate)=>{
    const from = (approvalDate === null || approvalDate === void 0 ? void 0 : approvalDate.gte) == null ? undefined : toApprovalDisplayDate(approvalDate.gte);
    const to = (approvalDate === null || approvalDate === void 0 ? void 0 : approvalDate.lte) == null ? undefined : toApprovalDisplayDate(approvalDate.lte);
    if (from == null && to == null) return {};
    return {
        [FilterType.ApprovalDate]: {
            dateRangeOption: DateRange.Custom,
            dateRange: [
                from,
                to
            ]
        }
    };
};
const mapLastBilled = (lastBilled)=>{
    if (lastBilled == null) return {};
    if (lastBilled.exists === false) return {
        [FilterType.LastBillingDate]: {
            type: LastBillingDateType.Never
        }
    };
    if (lastBilled.gte != null && lastBilled.lte != null) {
        const range = {
            type: LastBillingDateType.Range,
            fromDate: new Date(lastBilled.gte),
            toDate: new Date(lastBilled.lte)
        };
        return {
            [FilterType.LastBillingDate]: range
        };
    }
    if (lastBilled.exists === true) return {
        [FilterType.LastBillingDate]: {
            type: LastBillingDateType.Ever
        }
    };
    return {};
};
const mapPaymentMethod = (paymentMethod)=>{
    if ((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.in) == null) return {};
    return {
        [FilterType.PaymentMethod]: paymentMethod.in.map((value)=>PAYMENT_METHOD_MAP[value])
    };
};
const mapHasCredit = (hasCredit)=>{
    if ((hasCredit === null || hasCredit === void 0 ? void 0 : hasCredit.in) == null) return {};
    return {
        [FilterType.HasCredit]: hasCredit.in.map((value)=>HAS_CREDIT_MAP[value])
    };
};
const mapAmendments = (amendmentStatus)=>{
    if ((amendmentStatus === null || amendmentStatus === void 0 ? void 0 : amendmentStatus.in) == null) return {};
    return {
        [FilterType.AmendmentsStatus]: amendmentStatus.in.map((value)=>AMENDMENT_STATUS_MAP[value])
    };
};
const mapServices = (services)=>{
    var _serviceNamesToSet_in, _ref, _ref1;
    var _service_recurrence, _service_billedUpon, _service_completionState, _service_automaticState, _service_billedUpon1;
    const service = services === null || services === void 0 ? void 0 : services.any;
    if (service == null) return {};
    const serviceNames = (_serviceNamesToSet_in = serviceNamesToSet(service).in) !== null && _serviceNamesToSet_in !== void 0 ? _serviceNamesToSet_in : [];
    const occurrence = [
        ...((_ref = (_service_recurrence = service.recurrence) === null || _service_recurrence === void 0 ? void 0 : _service_recurrence.in) !== null && _ref !== void 0 ? _ref : []).map((value)=>RECURRENCE_MAP[value]),
        ...((_ref1 = (_service_billedUpon = service.billedUpon) === null || _service_billedUpon === void 0 ? void 0 : _service_billedUpon.in) !== null && _ref1 !== void 0 ? _ref1 : []).map((value)=>BILLED_UPON_OCCURRENCE_MAP[value]).filter((value)=>value != null)
    ];
    return _object_spread({}, serviceNames.length > 0 && {
        [FilterType.ServiceNames]: serviceNames
    }, ((_service_completionState = service.completionState) === null || _service_completionState === void 0 ? void 0 : _service_completionState.in) != null && {
        [FilterType.Completion]: service.completionState.in.map((value)=>COMPLETION_MAP[value])
    }, ((_service_automaticState = service.automaticState) === null || _service_automaticState === void 0 ? void 0 : _service_automaticState.in) != null && {
        [FilterType.Automatic]: service.automaticState.in.map((value)=>AUTOMATIC_STATE_MAP[value])
    }, ((_service_billedUpon1 = service.billedUpon) === null || _service_billedUpon1 === void 0 ? void 0 : _service_billedUpon1.in) != null && {
        [FilterType.BillingTrigger]: [
            ...new Set(service.billedUpon.in.map((value)=>BILLED_UPON_TRIGGER_MAP[value]))
        ]
    }, occurrence.length > 0 && {
        [FilterType.Recurrence]: [
            ...new Set(occurrence)
        ]
    });
};
const mapServicePriceIncrease = (services)=>{
    var _services_any_priceIncrease, _services_any, _services_none_priceIncrease, _services_none;
    if ((services === null || services === void 0 ? void 0 : (_services_any = services.any) === null || _services_any === void 0 ? void 0 : (_services_any_priceIncrease = _services_any.priceIncrease) === null || _services_any_priceIncrease === void 0 ? void 0 : _services_any_priceIncrease.exists) === true) {
        return {
            [FilterType.ServicePriceIncreaseState]: [
                PriceIncreaseFilterValue.Automatic
            ]
        };
    }
    if ((services === null || services === void 0 ? void 0 : (_services_none = services.none) === null || _services_none === void 0 ? void 0 : (_services_none_priceIncrease = _services_none.priceIncrease) === null || _services_none_priceIncrease === void 0 ? void 0 : _services_none_priceIncrease.exists) === true) {
        return {
            [FilterType.ServicePriceIncreaseState]: [
                PriceIncreaseFilterValue.None
            ]
        };
    }
    return {};
};
export const filterToFixedControls = (filter)=>_object_spread({}, mapStatus(filter.status), mapEffectiveDate(filter.effectiveDate), mapApprovalDate(filter.approvalDate), mapLastBilled(filter.lastBilled), mapPaymentMethod(filter.paymentMethod), mapHasCredit(filter.hasCredit), mapAmendments(filter.amendmentStatus), mapServices(filter.services), mapServicePriceIncrease(filter.services));
